import type { FC } from 'react';
import React, { useCallback } from 'react';
import Slider from 'react-slick';
import { Box } from '@withjoy/joykit';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SliderContainer } from './PhotoSlider.styles';
import type { Photo, BrannanPhotoSliderProps } from './PhotoSlider.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { useSliderSettings } from './hooks/useSliderSettings';
import { PHOTO_COUNT_TO_SHOW, FULL_WIDTH_PROPERTIES, PAGE_WIDTH_PROPERTIES } from './PhotoSlider.constants';
import { CONTAINER_ASPECT_RATIO } from './PhotoSlider.config';
import { getSsrHeroSizes } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto/ssrHeroSizes';

export const BrannanPhotoSlider: FC<BrannanPhotoSliderProps> = props => {
  const { photos, showPlaceholder, aspectRatio = CONTAINER_ASPECT_RATIO, photoRenderer: PhotoRenderer, crossfadeDelayMs, isPageWidth = false } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);

  const [containerDimensions, containerRef] = useSizeObserver();
  const { settings, sliderRef } = useSliderSettings();

  // `isPageWidth` already distinguishes the viewport-width slider (photoSlider) from the
  // page-column one (photoSliderPageWidth) — reuse it rather than threading a separate literal.
  // Note: PhotoTrio's mobile fallback also renders this component without setting isPageWidth,
  // even when it is standing in for PhotoTrioPageWidth — see PhotoTrio.tsx.
  const ssrSizes = getSsrHeroSizes(isPageWidth ? 'photoSliderPageWidth' : 'photoSlider');

  // react-slick's `fade: true` mode mounts every slide in the DOM at once (hidden slides sit at
  // opacity: 0, not display: none), so eager-SSR attributes must be scoped to the first slide
  // only — otherwise every hidden slide (up to PHOTO_COUNT_TO_SHOW) would also render a sharp
  // `fetchpriority="high"` <img> and start downloading a full-size srcset candidate on mount,
  // competing with the visible slide's own fetch for the LCP element.
  const getPhotoComponent = useCallback(
    (photo: Photo, index: number) => (
      <Box
        key={photo.id}
        width="100%"
        aspectRatio={`${aspectRatio}`}
        height="auto"
      >
        <PhotoRenderer
          photoUrl={photo.photoUrl}
          cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
          width={containerDimensions.width}
          height={containerDimensions.width / aspectRatio}
          containerType="fixed-aspect"
          crossfadeDelayMs={crossfadeDelayMs}
          ssrSizes={index === 0 ? ssrSizes : undefined}
        />
      </Box>
    ),
    [containerDimensions.width, aspectRatio, PhotoRenderer, crossfadeDelayMs, ssrSizes],
  );

  return (
    <SliderContainer
      ref={containerRef}
      height="100%"
      position="relative"
      aspectRatio={filledPhotos.length > 0 ? `${aspectRatio}` : undefined}
      {...(isPageWidth ? PAGE_WIDTH_PROPERTIES : FULL_WIDTH_PROPERTIES)}
    >
      <Slider
        ref={sliderRef}
        {...settings}
      >
        {filledPhotos.map((photo, index) => {
          return photo.photoUrl ? (
            getPhotoComponent(photo, index)
          ) : (
            <Box
              key={index}
              width="100%"
              height="auto"
              aspectRatio={`${aspectRatio}`}
              backgroundColor="lightgray"
            />
          );
        })}
      </Slider>
    </SliderContainer>
  );
};

BrannanPhotoSlider.displayName = 'BrannanPhotoSlider';
